import React, { useRef, useEffect, useState } from 'react';
import { X, Save, AlertTriangle, Check } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface ProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  email: string;
  initialData?: UserPreferences;
  onUpdate: (preferences: UserPreferences) => void;
}

export interface UserPreferences {
  display_name?: string;
  age?: number;
  grade?: string;
  interests?: string[];
  interaction_style?: 'casual' | 'formal' | 'fun' | 'professional';
}

const GRADES = [
  'Kindergarten',
  ...Array.from({ length: 12 }, (_, i) => `${i + 1}th Grade`),
  'Not in School'
];

const INTERESTS = [
  'Art',
  'Music',
  'Sports',
  'Science',
  'Technology',
  'Literature',
  'History',
  'Mathematics',
  'Languages',
  'Gaming'
];

const INTERACTION_STYLES = [
  { value: 'casual', label: 'Casual - Friendly and relaxed' },
  { value: 'formal', label: 'Formal - Academic and structured' },
  { value: 'fun', label: 'Fun - Playful and entertaining' },
  { value: 'professional', label: 'Professional - Clear and efficient' }
];

function ProfileModal({ isOpen, onClose, email, initialData, onUpdate }: ProfileModalProps) {
  const [preferences, setPreferences] = useState<UserPreferences>(initialData || {});
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [hasChanges, setHasChanges] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const saveTimeoutRef = useRef<number>();

  useEffect(() => {
    setPreferences(initialData || {});
  }, [initialData]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        if (hasChanges) {
          if (confirm('You have unsaved changes. Are you sure you want to close?')) {
            onClose();
          }
        } else {
          onClose();
        }
      }
    }

    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        if (hasChanges) {
          if (confirm('You have unsaved changes. Are you sure you want to close?')) {
            onClose();
          }
        } else {
          onClose();
        }
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose, hasChanges]);

  const updatePreference = (update: Partial<UserPreferences>) => {
    setPreferences(prev => {
      const newPreferences = { ...prev, ...update };
      setHasChanges(JSON.stringify(newPreferences) !== JSON.stringify(initialData));
      return newPreferences;
    });
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setSaveStatus('idle');
      
      const { data: userData, error: authError } = await supabase.auth.getSession();
      if (authError) throw authError;

      if (userData.session?.user.id) {
        const { error } = await supabase
          .from('profiles')
          .update({
            display_name: preferences.display_name,
            preferences: {
              age: preferences.age,
              grade: preferences.grade,
              interests: preferences.interests,
              interaction_style: preferences.interaction_style
            }
          })
          .eq('id', userData.session.user.id);

        if (error) throw error;

        onUpdate(preferences);
        setSaveStatus('success');
        setHasChanges(false);
        
        if (saveTimeoutRef.current) {
          window.clearTimeout(saveTimeoutRef.current);
        }
        saveTimeoutRef.current = window.setTimeout(() => {
          setSaveStatus('idle');
          onClose();
        }, 2000);
      }
    } catch (error) {
      console.error('Error saving preferences:', error);
      setSaveStatus('error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setPreferences(initialData || {});
    setHasChanges(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div
        ref={modalRef}
        className="bg-slate-900/90 backdrop-blur-xl border border-slate-700/50 rounded-xl p-6 max-w-md w-full relative"
      >
        <button
          onClick={() => {
            if (hasChanges) {
              if (confirm('You have unsaved changes. Are you sure you want to close?')) {
                onClose();
              }
            } else {
              onClose();
            }
          }}
          className="absolute -right-2 -top-2 w-12 h-12 flex items-center justify-center rounded-xl hover:bg-slate-800/50 text-slate-400 hover:text-slate-200 transition-all duration-300 backdrop-blur-sm"
          aria-label="Close modal"
        >
          <div className="bg-slate-900/90 w-8 h-8 rounded-lg flex items-center justify-center">
            <X className="w-5 h-5" />
          </div>
        </button>

        <div className="mb-6">
          <h2 className="text-2xl font-display font-bold bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400 bg-clip-text text-transparent drop-shadow-[0_0_15px_rgba(59,130,246,0.3)]">
            Profile Settings
          </h2>
          <p className="text-sm bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400 bg-clip-text text-transparent">
            Customize your learning experience
          </p>
        </div>

        <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
          <div>
            <label className="block text-sm font-display font-bold bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400 bg-clip-text text-transparent mb-1">
              Preferred Name
            </label>
            <input
              type="text"
              value={preferences.display_name || ''}
              onChange={(e) => updatePreference({ display_name: e.target.value })}
              className="w-full bg-slate-800/50 border border-slate-700/50 rounded-lg px-4 py-2.5 text-slate-100 placeholder:text-slate-500 focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/20 transition-all"
              placeholder="How should Rumi address you?"
            />
          </div>

          <div>
            <label className="block text-sm font-display font-bold bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400 bg-clip-text text-transparent mb-1">
              Age
            </label>
            <input
              type="number"
              min="1"
              max="120"
              value={preferences.age || ''}
              onChange={(e) => updatePreference({ age: parseInt(e.target.value) || undefined })}
              className="w-full bg-slate-800/50 border border-slate-700/50 rounded-lg px-4 py-2.5 text-slate-100 placeholder:text-slate-500 focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/20 transition-all"
            />
          </div>

          <div>
            <label className="block text-sm font-display font-bold bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400 bg-clip-text text-transparent mb-1">
              Grade Level
            </label>
            <select
              value={preferences.grade || ''}
              onChange={(e) => updatePreference({ grade: e.target.value })}
              className="w-full bg-slate-800/50 border border-slate-700/50 rounded-lg px-4 py-2.5 text-slate-100 focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/20 transition-all"
            >
              <option value="">Select grade level</option>
              {GRADES.map(grade => (
                <option key={grade} value={grade}>{grade}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-display font-bold bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400 bg-clip-text text-transparent mb-2">
              Interests
            </label>
            <div className="flex flex-wrap gap-2">
              {INTERESTS.map(interest => (
                <button
                  key={interest}
                  type="button"
                  onClick={() => {
                    updatePreference({
                      interests: preferences.interests?.includes(interest)
                        ? preferences.interests.filter(i => i !== interest)
                        : [...(preferences.interests || []), interest]
                    });
                  }}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-300 ${
                    preferences.interests?.includes(interest)
                      ? 'bg-gradient-to-r from-blue-500/20 to-purple-500/20 text-blue-300 border border-blue-500/30 shadow-lg shadow-blue-500/10'
                      : 'bg-slate-800/50 text-slate-400 border border-slate-700/50 hover:border-slate-600/50'
                  }`}
                >
                  {interest}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-display font-bold bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400 bg-clip-text text-transparent mb-1">
              Interaction Style
            </label>
            <select
              value={preferences.interaction_style || ''}
              onChange={(e) => updatePreference({ interaction_style: e.target.value as UserPreferences['interaction_style'] })}
              className="w-full bg-slate-800/50 border border-slate-700/50 rounded-lg px-4 py-2.5 text-slate-100 focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/20 transition-all"
            >
              <option value="">Select style</option>
              {INTERACTION_STYLES.map(style => (
                <option key={style.value} value={style.value}>{style.label}</option>
              ))}
            </select>
          </div>

          {saveStatus === 'error' && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 flex items-center space-x-2">
              <AlertTriangle className="w-4 h-4 text-red-400" />
              <p className="text-sm text-red-400">Failed to save changes. Please try again.</p>
            </div>
          )}

          {saveStatus === 'success' && (
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3 flex items-center space-x-2">
              <Check className="w-4 h-4 text-emerald-400" />
              <p className="text-sm text-emerald-400">Changes saved successfully!</p>
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-3 pt-4 mt-4 border-t border-slate-800/50">
          <button
            type="button"
            onClick={handleReset}
            disabled={!hasChanges || isSaving}
            className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Reset
          </button>
          <button
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
            className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white rounded-lg px-4 py-2 text-sm font-medium flex items-center space-x-2 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-blue-500/20 hover:shadow-indigo-500/20"
          >
            {isSaving ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                <span>Save Changes</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ProfileModal;